import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { addFieldMarkers, countFieldCandidates, listFields, updateField, WidgetField } from '~/lib/widgetFields';
import { T } from './tokens';

type Props = {
  html: string;
  onChange: (html: string) => void;
};

const useStyles = createUseStyles({
  root: { display: 'grid', gap: 14, color: T.fg, fontSize: 13 },
  intro: { margin: 0, color: T.muted, lineHeight: 1.5 },
  field: { display: 'grid', gap: 6 },
  label: { fontSize: 12, fontWeight: 600, color: T.fg, display: 'flex', gap: 8, alignItems: 'baseline' },
  kind: { fontFamily: T.mono, fontSize: 11, color: T.muted, fontWeight: 400 },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    fontSize: 13,
    border: `1px solid ${T.fieldLine}`,
    borderRadius: 6,
    background: T.field,
    color: T.fg,
    fontFamily: 'inherit',
    '&:focus': { outline: `2px solid ${T.fg}`, outlineOffset: 1 }
  },
  code: { fontFamily: T.mono, fontSize: 12 },
  pair: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8 },
  thumb: { width: 72, height: 48, objectFit: 'cover', borderRadius: 4, border: `1px solid ${T.line}`, background: T.chrome },
  imageRow: { display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr)', gap: 10, alignItems: 'start' },
  button: {
    justifySelf: 'start',
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 980,
    border: `1px solid ${T.fg}`,
    background: T.field,
    color: T.fg,
    cursor: 'pointer'
  }
});

const KIND_LABEL: Record<WidgetField['type'], string> = { text: 'text', richtext: 'text with formatting', link: 'link', image: 'image' };

const FieldsPanel: React.FC<Props> = ({ html, onChange }) => {
  const c = useStyles();
  const fields = useMemo(() => listFields(html), [html]);
  const candidates = useMemo(() => (fields.length ? 0 : countFieldCandidates(html)), [html, fields.length]);

  if (!fields.length) {
    return (
      <div className={c.root} data-testid="fields-panel">
        <p className={c.intro}>
          {candidates
            ? `Genesis found ${candidates} pieces of text, links and images it can make editable here, so changes don’t need code.`
            : 'Nothing here can be edited as a field. Ask your AI tool to add data-gx-field to the text you want to change.'}
        </p>
        {candidates > 0 && (
          <button type="button" className={c.button} onClick={() => onChange(addFieldMarkers(html))}>Make text editable</button>
        )}
      </div>
    );
  }

  const set = (key: string, patch: Parameters<typeof updateField>[2]) => onChange(updateField(html, key, patch));

  return (
    <div className={c.root} data-testid="fields-panel">
      {fields.map(f => (
        <div key={f.key} className={c.field} data-field={f.key}>
          <label className={c.label} htmlFor={`gx-field-${f.key}`}>
            {f.key.replace('#', ' ')} <span className={c.kind}>{KIND_LABEL[f.type]}</span>
          </label>
          {f.type === 'text' && (
            f.value.length > 80
              ? <textarea id={`gx-field-${f.key}`} className={c.input} rows={3} value={f.value} onChange={e => set(f.key, { value: e.target.value })} />
              : <input id={`gx-field-${f.key}`} className={c.input} value={f.value} onChange={e => set(f.key, { value: e.target.value })} />
          )}
          {f.type === 'richtext' && (
            <textarea id={`gx-field-${f.key}`} className={`${c.input} ${c.code}`} rows={2} value={f.value} onChange={e => set(f.key, { value: e.target.value })} />
          )}
          {f.type === 'link' && (
            <div className={c.pair}>
              <input id={`gx-field-${f.key}`} className={c.input} aria-label="Link text" value={f.value} onChange={e => set(f.key, { value: e.target.value })} />
              <input className={`${c.input} ${c.code}`} aria-label="Link URL" value={f.href || ''} onChange={e => set(f.key, { href: e.target.value })} />
            </div>
          )}
          {f.type === 'image' && (
            <div className={c.imageRow}>
              {f.src ? <img className={c.thumb} src={f.src} alt="" /> : <span className={c.thumb} />}
              <div className={c.field}>
                <input id={`gx-field-${f.key}`} className={`${c.input} ${c.code}`} aria-label="Image URL" placeholder="https://genesis-cdn.ff.com/…" value={f.src || ''} onChange={e => set(f.key, { src: e.target.value })} />
                <input className={c.input} aria-label="Image description (alt text)" placeholder="Describe the image" value={f.alt || ''} onChange={e => set(f.key, { alt: e.target.value })} />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default FieldsPanel;
